import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';

import { SnackbarOpen } from '#app/Action/SnackbarAction';
import { TableReload } from '#app/Action/TableAction';
import { dispatch } from '#app/App';
import AutoCompleteComponent from '#app/Component/AutoCompleteComponent';
import ButtonComponent from '#app/Component/ButtonComponent';
import SelectComponent from '#app/Component/SelectComponent';
import TextFieldComponent from '#app/Component/TextFieldComponent';
import ApiHandler from '#app/Handler/ApiHandler';
import { Form, FormField, initFormHandler } from '#app/Handler/FormHandler';
import { OrderDocument, OrderDocumentType } from '#app/typings/orderDocument';
import { Option } from '#app/typings/utils';

type Props = {
  onClose: () => void;
};

const ReturnModal: React.FC<Props> = ({ onClose }) => {
  const classes = useStyles();
  const [orderDocument, setOrderDocument] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingDocuments, setLoadingDocuments] = useState<boolean>(false);
  const [documents, setDocuments] = useState<OrderDocument[]>([]);

  const getProductsForm = (orderDocumentId: number) => ({
    ...documents
      .find((document) => document.id === orderDocumentId)
      ?.orderDocumentByProducts.filter(({ quantity }) => quantity > 0)
      .reduce((acc: Record<string, FormField>, { orderClientByProduct, quantity }) => {
        acc[orderClientByProduct.byProduct.toString()] = {
          name: orderClientByProduct.byProduct.toString(),
          label: 'Quantité',
          textHelper: `Saisissez la quantité de produit en litige (maximum : ${quantity})`,
          type: 'integer',
          options: { max: quantity },
        };
        return acc;
      }, {}),
  });

  const initForm: Form = {
    orderClient: {
      name: 'orderClient',
      label: 'Commande client',
      textHelper: 'Choisissez une commande client.',
      type: 'text',
      options: { validation: ['required'] },
    },
    reason: {
      name: 'reason',
      label: 'Raison',
      textHelper: 'Saisissez une raison.',
      type: 'text',
    },
    orderDocument: {
      name: 'orderDocument',
      label: 'Facture',
      textHelper: 'Selectionnez la facture en litige.',
      type: 'integer',
      options: { validation: ['required'] },
    },
  };

  const [form, setForm] = useState<Form>(initForm);
  const handlerForm = initFormHandler(form, setForm);
  useEffect(() => {
    handlerForm.start();
  }, []);

  useEffect(() => {
    if (orderDocument) {
      setForm({
        ...form,
        ...getProductsForm(orderDocument as number),
      });
    }
  }, [orderDocument]);

  const loadOrderClient = (valueText: string, callback: (options: Option[]) => void): void => {
    ApiHandler.get<Option[]>({ route: 'api_auto_complete_order_client', data: { text: valueText } }, (response) => {
      if (response.status === 200) {
        callback(response.data ?? []);
      }
    });
  };

  const loadByOrderClient = (orderClient: string): void => {
    setForm(initForm);
    handlerForm.setValue('orderDocument', undefined);
    setDocuments([]);
    setLoadingDocuments(true);
    ApiHandler.get<OrderDocument[]>(
      {
        route: 'api_order_document_list',
        params: { uuid: orderClient },
        data: { type: OrderDocumentType.INVOICE_ORDER_CLIENT },
      },
      (response) => {
        setDocuments(response.data ?? []);
        setLoadingDocuments(false);
        if (response.data?.length === 1) {
          setOrderDocument(response.data[0].id);
        }
      },
    );
  };

  const handleSubmit = (): void => {
    if (handlerForm.checkError() > 1) {
      return;
    }
    handlerForm.setFormLoading(true);
    const data = handlerForm.getData();
    const products = Object.entries(data)
      .filter(([key]) => !isNaN(parseInt(key)))
      .map(([key, value]) => ({
        quantity: value ?? 0,
        byProduct: parseInt(key),
      }));

    if (!products.some((product) => product.quantity > 0)) {
      dispatch(SnackbarOpen({ text: 'Il faut au moins un produit', variant: 'error' }));
      return;
    }

    handlerForm.setFormLoading(true);
    setLoading(true);

    ApiHandler.post(
      {
        route: 'api_inventory_management_return_order_add',
        data: {
          reason: data.reason,
          orderDocument: orderDocument as number,
          products,
        },
        params: { uuid: data.orderClient },
      },
      (response) => {
        const success = response.status === 200;
        const errorMessage = response.error?.message || "Une erreur inattendue s'est produite.";

        dispatch(
          SnackbarOpen({
            text: success ? 'Retour enregistré avec succès.' : errorMessage,
            variant: success ? 'success' : 'error',
          }),
        );

        if (success) {
          dispatch(TableReload('api_inventory_management_return_order_list'));
          onClose();
        }
        handlerForm.setFormLoading(false);
        setLoading(false);
      },
    );
  };

  return (
    <Dialog open maxWidth="xl" onClose={onClose}>
      <DialogTitle>Créer un retour</DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <AutoCompleteComponent
              id="orderClient"
              handler={handlerForm}
              loadOptions={loadOrderClient}
              onClick={(orderClient) => loadByOrderClient(orderClient as string)}
            />
          </Grid>
          <Grid size={12}>
            <TextFieldComponent id="reason" handler={handlerForm} multiline />
          </Grid>
          {loadingDocuments && <CircularProgress size={20} />}
          {documents.length > 1 && (
            <Grid size={12}>
              <SelectComponent
                id="orderDocument"
                options={documents.map((document) => ({
                  value: document.id,
                  label: document.reference,
                }))}
                handler={handlerForm}
                onChange={(value) => setOrderDocument(value as number)}
              />
            </Grid>
          )}

          {documents
            .find((document) => document.id === orderDocument)
            ?.orderDocumentByProducts.map(
              (orderDocumentByProduct) =>
                !!form[orderDocumentByProduct.orderClientByProduct.byProduct] && (
                  <React.Fragment key={orderDocumentByProduct.orderClientByProduct.byProduct}>
                    <Grid size={6} className={classes.productLabel}>
                      {orderDocumentByProduct.productLabel}
                    </Grid>
                    <Grid size={6}>
                      <TextFieldComponent
                        id={orderDocumentByProduct.orderClientByProduct.byProduct.toString()}
                        handler={handlerForm}
                      />
                    </Grid>
                  </React.Fragment>
                ),
            )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonComponent color="#5E6E82" label="Annuler" onClick={onClose} disabled={loading} />
        <ButtonComponent label="Valider" loading={loading} onClick={handleSubmit} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles({
  content: {
    minWidth: 300,
    width: '90vw',
    maxWidth: 800,
  },
  productLabel: {
    paddingTop: '10px',
  },
});

export default ReturnModal;
